import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

interface PublicRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
  redirectIfAuthenticated?: boolean;
}

/**
 * PublicRoute component
 *
 * For routes that should only be accessible when NOT authenticated (like login, signup).
 * Optionally redirects authenticated users to a different page.
 *
 * @param children - The component to render
 * @param redirectTo - Where to redirect authenticated users (defaults to `/`,
 *   the smart home that lands the member in their resolved view mode)
 * @param redirectIfAuthenticated - Whether to redirect if authenticated (defaults to true)
 */
export const PublicRoute: React.FC<PublicRouteProps> = ({
  children,
  redirectTo = '/',
  redirectIfAuthenticated = true,
}) => {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner spinner-lg text-primary-600 dark:text-primary-350" aria-label="Loading..." />
      </div>
    );
  }

  // If user is authenticated and we should redirect, send them to dashboard
  if (isAuthenticated && redirectIfAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  // Render the public page
  return <>{children}</>;
};

export default PublicRoute;
